import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiEvent, ApiList,
  apiAuthList, apiDeleteEvent, apiGetEvents, apiGetList, apiReorder, apiUpdateEvent, apiCreateEvent, apiUpdateListOptions,
  clearToken, getToken, storeToken
} from '@/api';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { downloadICSForEvent } from '@/utils/event';
import { useToast } from '@/hooks/useToast';
import { useNow } from '@/hooks/useNow';
import { TopBar } from '@/components/TopBar';
import { UnlockSheet } from '@/components/UnlockSheet';
import { LockedCard } from '@/components/cards/LockedCard';
import { EditableCard } from '@/components/cards/EditableCard';
import { ComposerCard } from '@/components/cards/ComposerCard';

export default function ListView() {
  const { code = '' } = useParams();
  const nav = useNavigate();

  const [list, setList] = useState<ApiList | null>(null);
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [canEdit, setCanEdit] = useState<boolean>(!!getToken());
  const [showArchived, setShowArchived] = useState(false);
  const [updatingSortMode, setUpdatingSortMode] = useState(false);
  const { msg, show } = useToast();

  // live clock: re-renders every second
  const now = useNow(1000);

  async function loadAll(archived = false) {
    if (!code) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [l, e] = await Promise.all([
        apiGetList(code),
        apiGetEvents(code, { archived })
      ]);
      setList(l);
      setEvents(e.events);
    } catch (err) {
      const message = extractErrorMessage(err);
      setEvents([]);
      setList(null);
      if (/not found/i.test(message)) {
        show('List not found');
        nav('/');
      } else {
        setLoadError(message);
        show(message || 'Failed to load events');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadAll(showArchived); /* eslint-disable-next-line */ }, [code, showArchived]);

  // Title without the "Events • " prefix; keep "Archived • " when viewing archived
  const titleText = useMemo(
    () => {
      const base = list ? (list.displayName || list.code) : code;
      return showArchived ? `Archived • ${base}` : base;
    },
    [list, code, showArchived]
  );

  async function unlock(password: string) {
    const { token } = await apiAuthList(code, password);
    storeToken(token);
    setCanEdit(true);
    show('Editing enabled');
  }
  function lock() {
    clearToken();
    setCanEdit(false);
    show('Locked');
  }

  async function create(payload: { title: string; start_ts: string; end_ts: string; location: string }) {
    await apiCreateEvent(code, {
      title: payload.title,
      startDateTime: payload.start_ts,
      endDateTime: payload.end_ts,
      location: payload.location
    });
    await loadAll(showArchived);
    show('Event added');
  }

  async function save(ev: ApiEvent, patch: { title?: string; start_ts?: string; end_ts?: string; location?: string }) {
    await apiUpdateEvent(code, ev.id, {
      title: patch.title,
      startDateTime: patch.start_ts,
      endDateTime: patch.end_ts,
      location: patch.location
    });
    await loadAll(showArchived);
    show('Saved');
  }

  async function del(ev: ApiEvent) {
    await apiDeleteEvent(code, ev.id);
    await loadAll(showArchived);
    show('Deleted');
  }

  async function archive(ev: ApiEvent, archived: boolean) {
    await apiUpdateEvent(code, ev.id, { archived });
    await loadAll(showArchived);
    show(archived ? 'Archived' : 'Unarchived');
  }

  async function changeSortMode(mode: 'date' | 'manual') {
    if (!list || list.sortMode === mode || !canEdit) return;
    setUpdatingSortMode(true);
    try {
      const updated = await apiUpdateListOptions(code, { sortMode: mode });
      setList(prev => prev ? { ...prev, sortMode: updated.sortMode } : updated);
      await loadAll(showArchived);
      show(mode === 'manual' ? 'Manual ordering enabled' : 'Date ordering enabled');
    } catch (err) {
      const message = extractErrorMessage(err);
      show(message || 'Failed to update sort mode');
    } finally {
      setUpdatingSortMode(false);
    }
  }

  function onICS(ev: ApiEvent) {
    downloadICSForEvent(ev);
  }

  async function onDragEnd(result: DropResult) {
    if (!result.destination || !list || list.sortMode !== 'manual' || !canEdit) return;
    const items = Array.from(events);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setEvents(items);
    try {
      await apiReorder(code, items.map(e => e.id));
      show('Reordered');
    } catch {
      await loadAll(showArchived);
    }
  }

  return (
    <main id="app" role="main" aria-labelledby="events-title">
      <a className="skip-link" href="#maincontent">Skip to content</a>
      <span id="maincontent" className="sr-only">Main content</span>

      <section id="events-view" className="view" aria-labelledby="events-title">
        <div id="events-shell" className="phone">
          <TopBar
            title={titleText}
            canEdit={canEdit}
            showArchived={showArchived}
            onBack={() => { lock(); nav('/'); }}
            onToggleArchived={() => setShowArchived(v => !v)}
            onToggleLock={() => canEdit ? lock() : setUnlockOpen(true)}
          />

          {canEdit && !showArchived && list && (
            <div className="sort-mode-control" role="group" aria-label="Sort order">
              <label htmlFor="sort-mode-select">Sort order</label>
              <select
                id="sort-mode-select"
                value={list.sortMode}
                onChange={(e) => { const val = e.target.value as 'date' | 'manual'; void changeSortMode(val); }}
                disabled={updatingSortMode}
              >
                <option value="date">Automatic (by date)</option>
                <option value="manual">Manual (drag & drop)</option>
              </select>
              {updatingSortMode && <span className="hint">Saving…</span>}
            </div>
          )}

          {loading ? (
            <div className="muted">Loading…</div>
          ) : loadError ? (
            <div className="error" role="alert">{loadError}</div>
          ) : (
            list?.sortMode === 'manual' && canEdit && !showArchived ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="events">
                  {(provided) => (
                    <div className="cards" role="list" ref={provided.innerRef} {...provided.droppableProps}>
                      <ComposerCard onAdd={create} />
                      {events.map((ev, idx) => (
                        <Draggable draggableId={ev.id} index={idx} key={ev.id}>
                          {(p) => (
                            <div ref={p.innerRef} {...p.draggableProps}>
                              <EditableCard
                                ev={ev}
                                manual
                                inArchivedView={false}
                                dragHandleProps={p.dragHandleProps ?? undefined}
                                onSave={(patch) => save(ev, patch)}
                                onDelete={() => del(ev)}
                                onArchive={(archived) => archive(ev, archived)}
                                now={now}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      <div className="footer-hint">Manual order — drag to reorder.</div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <div className="cards" role="list">
                {canEdit && !showArchived && <ComposerCard onAdd={create} />}
                {events.map(ev => (
                  canEdit ? (
                    <EditableCard
                      key={ev.id}
                      ev={ev}
                      manual={false}
                      inArchivedView={!!showArchived}
                      onSave={(patch) => save(ev, patch)}
                      onDelete={() => del(ev)}
                      onArchive={(archived) => archive(ev, archived)}
                      now={now}
                    />
                  ) : (
                    <LockedCard key={ev.id} ev={ev} onICS={() => onICS(ev)} now={now} />
                  )
                ))}
                {!canEdit && !showArchived && <div className="footer-hint">Read‑only. Unlock to edit and add events.</div>}
                {showArchived && <div className="footer-hint">Viewing archived events.</div>}
              </div>
            )
          )}
        </div>
      </section>

      <UnlockSheet open={unlockOpen} onClose={()=>setUnlockOpen(false)} onUnlock={unlock} />
      <div className={`toast glass ${msg ? 'show' : ''}`} role="status" aria-live="polite" aria-atomic="true">{msg}</div>
    </main>
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof Error && typeof err.message === 'string') {
    const raw = err.message.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'string') return parsed;
        if (parsed && typeof parsed === 'object') {
          if (typeof (parsed as any).error === 'string') return (parsed as any).error;
          if (typeof (parsed as any).message === 'string') return (parsed as any).message;
        }
      } catch {
        // not JSON — fall through to raw text
      }
      return raw;
    }
  }
  return 'Request failed';
}
